import { CarouselModal } from '@/components/common/carousel-modal';
import { ModalButton } from '@/components/common/modal-button';
import { ModalTransportationButton } from '@/components/common/modal-transportation-button';
import { PoiListCarousel } from '@/components/feature/floor-guide/poi-list-carousel';
import { LocationMarkerIcon, MarkerIcon, PhoneIcon, RouteIcon } from '@/components/icons';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import type { PoiContent } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { PoiContentItem } from '@/types/floor-guide';
import { AppUtils } from '@/utils/app';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';

type Props = {
    selectedPoi?: PoiContentItem;
    poiList?: PoiContent[];
    isOpen?: boolean;
    onClose?: () => void;
    showLocationConfirmation?: boolean;
    onGoToPoiLocation?: () => void;
    onSelectPoi?: (poi: PoiContent) => void;
};

export const PoiDetailModal = ({
    selectedPoi,
    poiList,
    isOpen = false,
    onClose = () => { },
    showLocationConfirmation = true,
    onGoToPoiLocation = () => { },
    onSelectPoi = () => { },
}: Props) => {
    const { t } = useTranslation();
    const currentLanguage = useConfigStore((state) => state.config.ui.language);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const { playTick } = useTickSoundInteraction();

    const { isHorizontal } = useOrientation();

    if (!isOpen) {
        return null;
    }

    return (
        <CarouselModal
            closeIconTabIndex={9}
            isOpen={isOpen}
            onClose={onClose}
            slides={poiList && <PoiListCarousel poiList={poiList} onSelectPoi={onSelectPoi} selectedPoiId={selectedPoi?.id} />}
        >
            <div className="widescreen:pt-[calc(24*var(--vh-unit))] h-full pt-[calc(36*var(--vh-unit))] pb-[calc(52*var(--vh-unit))]">
                <div className="text-text-main01 px-[calc(56*var(--vw-unit))] text-center text-[calc(40*var(--vh-unit))] font-bold">
                    <p data-modal-region tabIndex={1} data-interactive aria-label={selectedPoi?.name} className="focus-border line-clamp-1 text-ellipsis">
                        {selectedPoi?.name}
                    </p>
                </div>
                <div className="mt-[calc(24*var(--vh-unit))] flex items-center justify-center">
                    <div
                        className={clsx(
                            'focus-border h-[calc(560*var(--vh-unit))] w-[calc(568*var(--vw-unit))] rounded-md',
                            contrast && styles['logo--contrast']
                        )}
                        tabIndex={2}
                        data-interactive
                        aria-label={t('floor_guide.text_to_speech.logo_desc', { poiName: selectedPoi?.name })}
                    >
                        {selectedPoi?.imageSrc ? (
                            <img src={selectedPoi?.imageSrc} alt={selectedPoi?.name} className={clsx(['aspect-[568/560]'], [styles['img_contain']])} />
                        ) : (
                            <div className="h-full w-full">
                                <PlaceHolder />
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className={clsx({
                        'mt-[calc(16*var(--vh-unit))] grid grid-cols-[1fr_auto] items-center px-[calc(56*var(--vw-unit))] py-[calc(12*var(--vh-unit))]':
                            currentLanguage === 'ko',

                        'widescreen:h-[calc(132*var(--vh-unit))] grid h-[calc(156*var(--vh-unit))] grid-cols-[1fr] items-start gap-[calc(12*var(--vh-unit))] px-[calc(56*var(--vw-unit))] py-[calc(12*var(--vh-unit))]':
                            currentLanguage !== 'ko',
                    })}
                >
                    <div
                        data-interactive
                        tabIndex={3}
                        aria-label={`${t('common.tts.location')} ${selectedPoi?.showBuildingAddress ? selectedPoi?.address : ''
                            }, ${t('shop.search.tts.content.floor', {
                                floor: AppUtils.getFloorTTSText(selectedPoi?.floorName || ''),
                            })}, ${selectedPoi?.category}`}
                        className="flex min-w-0 items-center gap-[calc(12*var(--vw-unit))]"
                    >
                        <div className="flex-shrink-0">
                            <LocationMarkerIcon width={36} height={36} />
                        </div>

                        <div className="flex min-w-0 items-center gap-[calc(12*var(--vw-unit))]">
                            {selectedPoi?.showBuildingAddress && (
                                <>
                                    <span className="max-w-[calc(100*var(--vw-unit))] truncate text-[calc(24*var(--vh-unit))] leading-[calc(36*var(--vh-unit))] text-text-sub01">
                                        {selectedPoi?.address}
                                    </span>
                                    <div className="h-[calc(20*var(--vh-unit))] w-[calc(2*var(--vw-unit))] bg-text-sub01" />
                                </>
                            )}

                            {selectedPoi?.floorName && (
                                <span className="flex-shrink-0 text-[calc(26*var(--vh-unit))] font-bold text-text-main01 number">
                                    {selectedPoi?.floorName}
                                </span>
                            )}

                            {selectedPoi?.category && (
                                <span className="max-w-[calc(120*var(--vw-unit))] truncate text-[calc(24*var(--vh-unit))] leading-[calc(36*var(--vh-unit))] font-medium text-text-main01">
                                    {selectedPoi?.category}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* PHONE – AUTO WIDTH, KHÔNG BAO GIỜ CẮT */}
                    {selectedPoi?.phoneNumber && (
                        <div
                            tabIndex={4}
                            data-interactive
                            aria-label={`${t('common.tts.phone_number')} ${AppUtils.getPhoneNumberTTSText(
                                selectedPoi?.phoneNumber,
                            )}`}
                            className="flex items-center gap-[calc(8*var(--vw-unit))] whitespace-nowrap"
                        >
                            <PhoneIcon width={36} height={36} />
                            <span className="text-[calc(26*var(--vh-unit))] font-medium text-text-main01 number">
                                {selectedPoi?.phoneNumber}
                            </span>
                        </div>
                    )}
                </div>

                {!isHorizontal ? (
                    <div
                        className={`widescreen:hidden ${currentLanguage === 'ko' ? 'mt-[calc(40*var(--vh-unit))]' : ''} flex items-center justify-center gap-[calc(23*var(--vw-unit))]`}
                    >
                        {showLocationConfirmation && (
                            <ModalButton
                                tabIndex={5}
                                data-interactive
                                aria-label={t('common.button.location_confirmation')}
                                size="lg"
                                variant="primary"
                                icon={<MarkerIcon width={80} height={80} role="button" />}
                                data-role={t('common.data_role.button')}
                            >
                                {t('common.button.location_confirmation')}
                            </ModalButton>
                        )}
                        <ModalButton
                            tabIndex={6}
                            data-interactive
                            aria-label={t('common.button.direction')}
                            size="lg"
                            variant="secondary"
                            onClick={onGoToPoiLocation}
                            icon={<RouteIcon width={80} height={80} role="button" />}
                            data-role={t('common.data_role.button')}
                        >
                            {t('common.button.direction')}
                        </ModalButton>
                    </div>
                ) : (
                    <div
                        className={`widescreen:flex ${currentLanguage === 'ko' ? 'mt-[calc(40*var(--vh-unit))] hidden' : ''} items-center justify-center gap-[calc(23*var(--vw-unit))]`}
                    >
                        {showLocationConfirmation && (
                            <ModalTransportationButton
                                tabIndex={5}
                                data-interactive
                                aria-label={t('common.button.location_confirmation')}
                                variant="primary"
                                icon={<MarkerIcon width={60} height={60} role="button" />}
                                data-role={t('common.data_role.button')}
                            >
                                {t('common.button.location_confirmation')}
                            </ModalTransportationButton>
                        )}
                        <ModalTransportationButton
                            tabIndex={6}
                            data-interactive
                            aria-label={t('common.button.direction')}
                            variant="secondary"
                            onClick={onGoToPoiLocation}
                            icon={<RouteIcon width={60} height={60} role="button" />}
                            data-role={t('common.data_role.button')}
                        >
                            {t('common.button.direction')}
                        </ModalTransportationButton>
                    </div>
                )}
            </div>
        </CarouselModal>
    );
};
